import { z } from 'zod'

/**
 * Zod schemas for LeadForm - define schemas first, then infer types
 */

// Home Details schemas
const homeSizeSchema = z.enum(['apartment', 'small', 'large'])
const bedroomsSchema = z.enum(['1', '2', '3', '4', '5+'])
const bathroomsSchema = z.enum(['1', '2', '3', '4', '5+'])
const floorsSchema = z.enum(['1', '2', '3', '4+'])
const yearBuiltRangeSchema = z.enum([
  'before_1960',
  '1960-1969',
  '1970-1979',
  '1980-1989',
  '1990-1999',
  '2000-2009',
  '2010-2019',
  '2020-present',
  'dont_know',
])

const homeDetailsSchema = z.object({
  homeSize: homeSizeSchema,
  bedrooms: bedroomsSchema,
  bathrooms: bathroomsSchema,
  // An omitted key, never null: core-ranking accepts a home description with
  // no floor count, and nothing guesses one.
  floors: floorsSchema.optional(),
  yearBuilt: yearBuiltRangeSchema.nullable(),
})

// Address schema
const leadFormAddressSchema = z.object({
  zipcode: z.string().default(''),
  city: z.string().default(''),
  state: z.string().default(''),
})

// Customer Intent schemas
const intentSchema = z.enum(['switching_plans', 'moving_homes'])
const texasResidencySchema = z.enum(['moving_from_out_of_state', 'already_texas_resident'])

const residencyStatusSchema = z.object({
  texasResidency: texasResidencySchema,
})

const customerIntentSchema = z.object({
  intent: intentSchema,
})

// Energy Usage Profile schemas
const householdSizeSchema = z.enum(['1', '2', '3', '4', '5+'])
const minUsageDaysSchema = z.enum(['0', '1-2', '3-4', '5-6', '7'])

const energyUsageProfileSchema = z.object({
  householdSize: householdSizeSchema.nullable(),
  minUsageDays: minUsageDaysSchema.nullable(),
  summerThermostatFahrenheit: z.number().nullable(),
  winterThermostatFahrenheit: z.number().nullable(),
})

// Household Features schemas
const householdFeatureSchema = z.enum([
  'central_heating_ac',
  'natural_gas_heating',
  'pool_or_hot_tub',
  'smart_thermostat',
  'home_automation_energy_management',
  'electric_vehicle',
  'solar_panels',
])

const householdFeaturesSchema = z.object({
  features: z.array(householdFeatureSchema),
})

// Plan Preferences schemas
const preferredTermSchema = z.enum(['12_months', '24_months', '36_months', 'unsure'])

const planTermPreferencesSchema = z.object({
  preferredTerm: preferredTermSchema,
})

// EV Charging Preferences schemas
const chargingTimeSchema = z.enum([
  'full_night',
  'full_day',
  'varies_between_daytime_and_nighttime',
])

const evChargingPreferencesSchema = z.object({
  chargingTime: chargingTimeSchema,
})

// User Preferences schemas
const userPreferenceEnum = z.enum([
  'predictable_monthly_bill',
  'lowest_total_yearly_cost',
  'top_rated_service_support',
  'green_energy',
  'bill_credits',
])

const userPreferencesSchema = z.object({
  features: z.array(userPreferenceEnum),
})

/**
 * Main LeadForm schema with all fields optional for partial validation
 */
export const leadFormSchema = z.object({
  address: leadFormAddressSchema,
  homeDetails: homeDetailsSchema.nullable(),
  customerIntent: customerIntentSchema,
  residencyStatus: residencyStatusSchema.nullable().optional(),
  energyUsageProfile: energyUsageProfileSchema,
  householdFeatures: householdFeaturesSchema.nullable().optional(),
  planTermPreferences: planTermPreferencesSchema,
  evChargingPreferences: evChargingPreferencesSchema.nullable().optional(),
  preferences: userPreferencesSchema.nullable().optional(),
})

/**
 * Inferred TypeScript types from Zod schemas
 */
export type HomeSize = z.infer<typeof homeSizeSchema>
export type Bedrooms = z.infer<typeof bedroomsSchema>
export type Bathrooms = z.infer<typeof bathroomsSchema>
export type Floors = z.infer<typeof floorsSchema>
export type YearBuiltRange = z.infer<typeof yearBuiltRangeSchema>
export type HomeDetails = z.infer<typeof homeDetailsSchema>

export type Intent = z.infer<typeof intentSchema>
export type TexasResidency = z.infer<typeof texasResidencySchema>
export type ResidencyStatus = z.infer<typeof residencyStatusSchema>
export type CustomerIntent = z.infer<typeof customerIntentSchema>

export type HouseholdSize = z.infer<typeof householdSizeSchema>
export type MinUsageDays = z.infer<typeof minUsageDaysSchema>
export type EnergyUsageProfile = z.infer<typeof energyUsageProfileSchema>

export type HouseholdFeature = z.infer<typeof householdFeatureSchema>
export type HouseholdFeatures = z.infer<typeof householdFeaturesSchema>

export type PreferredTerm = z.infer<typeof preferredTermSchema>
export type PlanTermPreferences = z.infer<typeof planTermPreferencesSchema>

export type ChargingTime = z.infer<typeof chargingTimeSchema>
export type EVChargingPreferences = z.infer<typeof evChargingPreferencesSchema>

export type UserPreferences = z.infer<typeof userPreferencesSchema>
export type UserPreferenceEnum = z.infer<typeof userPreferenceEnum>

export type LeadFormAddress = z.infer<typeof leadFormAddressSchema>
export type LeadForm = z.infer<typeof leadFormSchema>
